import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { 
  DEFAULT_LANGUAGE, 
  getSavedLanguage, 
  saveLanguage, 
  getLanguageConfig,
  SUPPORTED_LANGUAGES 
} from '../constants/languageConfig';

// Import locale files
import enLocale from '../locales/en.json';
import jaLocale from '../locales/ja.json';
import zhHantLocale from '../locales/zh-hant.json';
import koLocale from '../locales/ko.json';

// Map of language codes to locale data
const LOCALES = {
  EN: enLocale,
  JP: jaLocale,
  'ZH-HANT': zhHantLocale,
  KR: koLocale,
};

// Create context
const LanguageContext = createContext(null);

/**
 * Language Provider Component
 * 
 * Provides language state and translation functions to the app.
 * Persists language selection to localStorage.
 */
export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(getSavedLanguage());
  const [translations, setTranslations] = useState(LOCALES[getSavedLanguage()] || LOCALES[DEFAULT_LANGUAGE]);

  // Update translations when language changes
  useEffect(() => {
    const locale = LOCALES[language] || LOCALES[DEFAULT_LANGUAGE];
    setTranslations(locale);
  }, [language]);

  // Change language and persist
  const setLanguage = useCallback((newLang) => {
    const code = (newLang || DEFAULT_LANGUAGE).toUpperCase();
    if (SUPPORTED_LANGUAGES[code]) {
      setLanguageState(code);
      saveLanguage(code);
    }
  }, []);

  // Get language config
  const languageConfig = getLanguageConfig(language);

  // Translation function with fallback
  const t = useCallback((key, params = {}) => {
    // Get nested key value (e.g., "sidebar.search.title")
    const keys = key.split('.');
    let value = translations;
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Fallback to English if key not found
        value = enLocale;
        for (const k2 of keys) {
          if (value && typeof value === 'object' && k2 in value) {
            value = value[k2];
          } else {
            // Return key as fallback
            console.warn(`Translation missing: ${key}`);
            return key;
          }
        }
        break;
      }
    }

    // Handle string interpolation: "Hello {name}" + {name: "World"} = "Hello World"
    if (typeof value === 'string' && Object.keys(params).length > 0) {
      return value.replace(/\{(\w+)\}/g, (_, paramKey) => {
        return params[paramKey] !== undefined ? params[paramKey] : `{${paramKey}}`;
      });
    }

    return value || key;
  }, [translations]);

  // Get available language codes
  const availableLanguages = Object.keys(SUPPORTED_LANGUAGES);

  const value = {
    language,           // Current language code (e.g., "EN", "JP")
    setLanguage,        // Function to change language
    languageConfig,     // Full language config object
    t,                  // Translation function
    isDefaultLanguage: language === DEFAULT_LANGUAGE,
    availableLanguages, // Array of supported language codes
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

/**
 * Hook to use language context
 */
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

/**
 * Hook for just the translation function (convenience)
 */
export function useTranslation() {
  const { t, language } = useLanguage();
  return { t, language };
}

export default LanguageContext;
